// #753 — Thin user-detail dashboard. After the tile-migration this page
// is no longer a long stack of flat sections; it's:
//   - User header (name / email / joined + Suspend / Factory reset /
//     Delete account action row)
//   - Token Usage bar (most-monitored metric — kept here for at-a-glance)
//   - 5-tile dashboard linking to each per-area sub-page
// Per-area data lives on AdminUserHealthConcerns / AdminUserConversations
// / AdminUserMemory / AdminUserNewsTopics / AdminUserSettings.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .back-link {
    display: inline-flex; align-items: center; gap: 6px;
    color: #888; font-size: 0.85rem; text-decoration: none;
    margin-bottom: 20px; transition: color 0.15s;
  }
  .back-link:hover { color: #E8845A; }
  .user-header {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 24px; margin-bottom: 20px;
    display: flex; align-items: flex-start; justify-content: space-between;
    flex-wrap: wrap; gap: 16px;
  }
  .user-name {
    font-size: 1.3rem; font-weight: 700; color: #2C2C2C;
    letter-spacing: -0.02em; margin-bottom: 4px;
  }
  .user-email { color: #888; font-size: 0.9rem; margin-bottom: 4px; }
  .user-meta { color: #aaa; font-size: 0.82rem; }

  /* #753 — unified account-action button set. Same shape / padding /
     typography across Suspend / Factory reset / Delete account; severity
     expressed via colour treatment only. */
  .account-actions {
    display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
  }
  .btn-account {
    padding: 9px 16px; border-radius: 8px;
    font-family: inherit; font-size: 0.85rem; font-weight: 600;
    background: #fff; cursor: pointer;
    border: 1.5px solid #E0DDD8; color: #555;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
    white-space: nowrap;
  }
  .btn-account:hover { transform: translateY(-1px); }
  .btn-account:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .btn-account.warning { border-color: #D9A227; color: #B08C00; }
  .btn-account.warning:hover { background: #D9A227; color: #fff; border-color: #D9A227; }
  .btn-account.success { border-color: #27AE60; color: #1F8A4A; }
  .btn-account.success:hover { background: #27AE60; color: #fff; border-color: #27AE60; }
  .btn-account.danger { border-color: #C0392B; color: #C0392B; }
  .btn-account.danger:hover { background: #C0392B; color: #fff; }
  .btn-account.danger-strong { background: #C0392B; color: #fff; border-color: #C0392B; }
  .btn-account.danger-strong:hover { background: #A02E22; border-color: #A02E22; }

  .section-title {
    font-size: 0.95rem; font-weight: 700; color: #2C2C2C;
    margin-bottom: 12px; letter-spacing: -0.01em;
  }
  .token-bar-wrap {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 20px 24px; margin-bottom: 24px;
  }
  .token-bar-label {
    display: flex; justify-content: space-between;
    font-size: 0.82rem; color: #888; margin-bottom: 8px;
  }
  .token-bar-bg {
    background: #EDE9E4; border-radius: 99px; height: 10px; overflow: hidden;
  }
  .token-bar-fill {
    height: 100%; border-radius: 99px;
    background: linear-gradient(90deg, #E8845A, #f0a07c);
    transition: width 0.5s ease;
  }
  .token-totals {
    display: flex; gap: 24px; margin-top: 12px;
    font-size: 0.85rem; color: #666;
  }
  .token-totals strong { color: #2C2C2C; }

  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }

  /* #753 — tile dashboard. All 5 tiles active post-migration; each
     links to its own sub-page. Mirrors the Family Portal pattern at
     Portal.js:27. */
  .tile-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 14px; margin-bottom: 24px;
  }
  .tile-card {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 18px 18px 16px;
    display: flex; flex-direction: column; gap: 6px;
    text-decoration: none; color: inherit;
    transition: transform 0.15s, box-shadow 0.15s;
    position: relative; cursor: pointer;
  }
  .tile-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 18px rgba(0,0,0,0.10);
  }
  .tile-icon { font-size: 1.6rem; line-height: 1; }
  .tile-title {
    font-size: 0.95rem; font-weight: 700; color: #2C2C2C;
    margin-top: 2px;
  }
  .tile-desc {
    font-size: 0.82rem; color: #888; line-height: 1.35;
  }
`;

function fmt(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [softResetting, setSoftResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    adminApi.getUser(id)
      .then(setUser)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  async function toggleSuspend() {
    setSuspending(true);
    try {
      const res = await adminApi.suspendUser(id);
      setUser(u => ({ ...u, suspended: res.suspended }));
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSuspending(false);
    }
  }

  async function factoryReset() {
    if (!window.confirm('This will permanently delete ALL data for this account and all linked elderly users. This cannot be undone. Click OK to confirm.')) return;
    setResetting(true);
    try {
      const res = await fetch(`/api/admin/users/${id}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('am_admin_token')}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Reset failed');
      alert('Account reset successfully.');
      navigate('/admin/users');
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setResetting(false);
    }
  }

  // #888 — soft reset: wipe runtime accumulation for the linked
  // elderly user but PRESERVE intake state (intake_sessions,
  // user_interests_structured, intake/portal-sourced memories,
  // tablet pairing, the users row itself). Re-seeds card_weights
  // to BASE_WEIGHT for each preserved interest. Distinct from
  // factory_reset which wipes everything (including intake — so
  // the family has to re-onboard). Use this to A/B-test runtime
  // behaviour from a known post-intake starting point without
  // re-typing intake answers each cycle.
  async function softReset() {
    const eid = user && user.elderly_user_id;
    if (!eid) {
      alert('No linked elderly user — soft reset has nothing to restore. Use factory reset to wipe the family account.');
      return;
    }
    if (!window.confirm(
      // #927 — copy uses the canonical `Engagement` vocabulary. The
      // backend tables (`card_weights`, `user_activity_preferences`)
      // are unchanged; only the admin-facing wording is unified.
      'Soft reset: wipe runtime data (conversations, learned ' +
      'engagement, extracted memories) but KEEP intake answers ' +
      '(interests, family-configured memories, tablet pairing). ' +
      'Family-portal-customised engagement values will be LOST and ' +
      're-seeded at the dealer default. Click OK to confirm.'
    )) return;
    setSoftResetting(true);
    try {
      const res = await fetch(`/api/admin/users/elderly/${eid}/soft-reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('am_admin_token')}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Soft reset failed');
      alert('Elderly user reset to post-intake state.');
      // No navigate — staying on the page is fine. Just refresh so
      // any cached panel data picks up the post-reset state on
      // sub-page visits.
      window.location.reload();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSoftResetting(false);
    }
  }

  // #228 — permanent account delete. Stronger gate than factoryReset:
  // the admin must type the user's email to confirm, since this also
  // removes the family_users row (login is gone, not just data wiped).
  async function deleteAccount() {
    const expected = (user && user.email) || '';
    const typed = window.prompt(
      `This permanently DELETES the account ${expected}, including the login.\n` +
      `Factory reset only wipes data; this also removes the family_users row.\n\n` +
      `Type the user's email exactly to confirm:`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== expected.trim().toLowerCase()) {
      alert("Email didn't match — delete aborted.");
      return;
    }
    setDeleting(true);
    try {
      await adminApi.deleteUser(id);
      alert('Account deleted permanently.');
      navigate('/admin/users');
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setDeleting(false);
    }
  }

  // #270 — bar shows current-month against the actual enforced quota
  // (env-driven, default 500k), not lifetime against a hardcoded 1M.
  const monthlyQuota = (user && user.monthly_quota) || 500000;
  const tokensThisMonth = (user && user.tokens_this_month) || 0;
  const barPct = user ? Math.min(100, (tokensThisMonth / monthlyQuota) * 100) : 0;
  const overQuota = user && user.within_quota === false;

  return (
    <AdminLayout title="User Detail">
      <style>{styles}</style>

      <Link to="/admin/users" className="back-link">← Back to users</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <>
          {/* #753 — unified account-action row in the header. Suspend /
              Factory reset / Delete account share one button style with
              severity by colour treatment. The matching standalone
              buttons at the bottom of the page were removed in the
              same change. */}
          <div className="user-header">
            <div>
              <div className="user-name">{user.name}</div>
              <div className="user-email">{user.email}</div>
              <div className="user-meta">Joined {fmtDate(user.created_at)}</div>
            </div>
            <div className="account-actions">
              <button
                className={`btn-account ${user.suspended ? 'success' : 'warning'}`}
                onClick={toggleSuspend}
                disabled={suspending || resetting || deleting}
              >
                {suspending
                  ? '…'
                  : user.suspended ? 'Unsuspend account' : 'Suspend account'}
              </button>
              {/* #888 — soft reset visible only when an elderly user
                  is linked. For families that haven't completed intake,
                  factory_reset is the right tool (no intake state to
                  preserve), so we hide soft reset rather than show it
                  disabled. */}
              {user.elderly_user_id && (
                <button
                  className="btn-account warning"
                  onClick={softReset}
                  disabled={softResetting || resetting || deleting || suspending}
                >
                  {softResetting ? 'Resetting…' : 'Soft reset (post-intake)'}
                </button>
              )}
              <button
                className="btn-account danger"
                onClick={factoryReset}
                disabled={resetting || deleting || suspending || softResetting}
              >
                {resetting ? 'Resetting…' : 'Factory reset'}
              </button>
              <button
                className="btn-account danger-strong"
                onClick={deleteAccount}
                disabled={deleting || resetting || suspending || softResetting}
              >
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>

          <div className="section-title">Token Usage</div>
          <div className="token-bar-wrap">
            <div className="token-bar-label">
              <span>
                This month
                {overQuota && (
                  <strong style={{ color: '#C0392B' }}> · over quota</strong>
                )}
              </span>
              <span>{fmt(tokensThisMonth)} / {fmt(monthlyQuota)}</span>
            </div>
            <div className="token-bar-bg">
              <div
                className="token-bar-fill"
                style={{
                  width: `${barPct}%`,
                  background: overQuota ? '#C0392B' : (barPct >= 80 ? '#E8845A' : undefined),
                }}
              />
            </div>
            <div className="token-totals">
              <span><strong>Lifetime:</strong> {fmt(user.tokens_total)} tokens</span>
              <span><strong>{fmt(user.tokens_input)}</strong> in / <strong>{fmt(user.tokens_output)}</strong> out</span>
            </div>
            {user.next_reset_date && (
              <div className="token-totals" style={{ marginTop: 8 }}>
                <span style={{ fontSize: '0.85rem' }}>
                  Resets {fmtDate(user.next_reset_date)}
                </span>
              </div>
            )}
          </div>

          {/* #753 — 5-tile dashboard. All five tiles route to their own
              per-area sub-page; the previous flat sections (Long-term
              Memory / News & Topics / Briefing History / Companion
              calls / Conversations / Quota / TTS / Audio profile / Mic
              recording) all moved out of this page in the same change. */}
          <div className="tile-grid">
            <Link to={`/admin/users/${id}/health-concerns`} className="tile-card">
              <div className="tile-icon">🩺</div>
              <div className="tile-title">Health Concerns</div>
              <div className="tile-desc">
                Confirmed concerns + the verify-pending audit trail.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/conversations`} className="tile-card">
              <div className="tile-icon">💬</div>
              <div className="tile-title">Conversations</div>
              <div className="tile-desc">
                Companion calls + raw transcripts.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/memory`} className="tile-card">
              <div className="tile-icon">🧠</div>
              <div className="tile-title">Memory</div>
              <div className="tile-desc">
                Long-term facts the companion has learned.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/reminders`} className="tile-card">
              <div className="tile-icon">⏰</div>
              <div className="tile-title">Reminders</div>
              <div className="tile-desc">
                All reminders (incl. voice-set) + callbacks.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/news-topics`} className="tile-card">
              <div className="tile-icon">📰</div>
              <div className="tile-title">News &amp; Topics</div>
              <div className="tile-desc">
                Topic affinities + briefing history.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/engagement`} className="tile-card">
              <div className="tile-icon">📈</div>
              <div className="tile-title">Engagement</div>
              <div className="tile-desc">
                Weekly engagement trajectory + slipping indicator.
              </div>
            </Link>
            {/* #767 Story F — per-user open-threads admin view. */}
            <Link to={`/admin/users/${id}/open-threads`} className="tile-card">
              <div className="tile-icon">🧵</div>
              <div className="tile-title">Open threads</div>
              <div className="tile-desc">
                Conversational arcs the user left unfinished — active +
                resolved history.
              </div>
            </Link>
            {/* #873 — per-user Cards sub-page (topic-activity charts + grid). */}
            <Link to={`/admin/users/${id}/cards`} className="tile-card">
              <div className="tile-icon">🃏</div>
              <div className="tile-title">Cards</div>
              <div className="tile-desc">
                Topic-activity dashboard + dealt-card history per call.
              </div>
            </Link>
            {/* #845 — per-user persona sub-page (read-only QA view). */}
            <Link to={`/admin/users/${id}/persona`} className="tile-card">
              <div className="tile-icon">🎭</div>
              <div className="tile-title">Persona</div>
              <div className="tile-desc">
                Berta's curated likes, dislikes, and quirks — for verifying
                she uses them on call.
              </div>
            </Link>
            <Link to={`/admin/users/${id}/settings`} className="tile-card">
              <div className="tile-icon">⚙️</div>
              <div className="tile-title">Settings</div>
              <div className="tile-desc">
                Quota, TTS provider, speaker profile, mic recording.
              </div>
            </Link>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
